import { useMemo, useState, useRef, useEffect, Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Navbar from "../components/Navbar/Navbar";
import { apiCall } from "../api/apiCall";
import { ExpenseReport, StatusType } from "../models/expense_report";
import "./ExpenseReportForm.css";
import { useCurrentUser } from "./UserCurrentUser";
import TopHeader from "../components/Navbar/TopHeader";
import { User } from "../models/user";
import FeatureGate from "../components/FeatureGate";
import { useTranslation } from "../i18n/UseTranslation";
import { downloadCSV } from "../utils/csvExport";
import { downloadXML } from "../utils/xmlExport";
import { downloadPDF } from "../utils/pdfExport";
import { getAuth, onAuthStateChanged } from "firebase/auth"; 


const PAGE_SIZE = 20;
const COLOR_CLASSES = ["avatar-blue", "avatar-teal", "avatar-amber", "avatar-purple", "avatar-coral"];

const _now       = new Date();
const PREV_DATE  = new Date(_now.getFullYear(), _now.getMonth() - 1, 1);
const CURRENT_MONTH = PREV_DATE.getMonth();
const CURRENT_YEAR  = PREV_DATE.getFullYear();

// ── Types ─────────────────────────────────────────────────────
interface CompanyFilter {
  month: string;   // "" | "YYYY-MM"
  year:  string;   // "" | "YYYY"
  category: string;
  status: string;
}

interface UserGroup {
  userId:       string;
  userName:     string;
  allReports:   ExpenseReport[];
  monthReports: ExpenseReport[];
  monthTotal:   number;
  pendingCount: number;
  approvedCount:number;
}

interface CompanyGroup {
  companyId:   string;
  companyName: string;
  users:       UserGroup[];
  monthTotal:  number;
}

// ── Helpers ───────────────────────────────────────────────────
function isCurrentMonth(date: Date | string | null): boolean {
  if (!date) return false;
  const d = new Date(date);
  return d.getMonth() === CURRENT_MONTH && d.getFullYear() === CURRENT_YEAR;
}

function formatAmount(n: number): string {
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function formatDate(date: Date | string | null): string {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function userInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
}

function companyInitials(name: string): string {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function groupData(reports: ExpenseReport[], t: any): CompanyGroup[] {
  const companyMap = new Map<string, { companyName: string; byUser: Map<string, ExpenseReport[]> }>();

  for (const r of reports) {
    const companyId   = r.company?.uuid ?? "__no_company__";
    const companyName = r.company?.name ?? t.expenseReports.noCompany;
    const userId      = r.user?.firebaseUid ?? "__no_user__";

    if (!companyMap.has(companyId)) companyMap.set(companyId, { companyName, byUser: new Map() });
    const comp = companyMap.get(companyId)!;
    if (!comp.byUser.has(userId)) comp.byUser.set(userId, []);
    comp.byUser.get(userId)!.push(r);
  }

  const result: CompanyGroup[] = [];

  for (const [companyId, { companyName, byUser }] of companyMap) {
    const users: UserGroup[] = [];
    for (const [userId, userReports] of byUser) {
      const first        = userReports[0].user;
      const firstName    = first?.first_name ?? "";
      const lastName     = first?.last_name  ?? "";
      const monthReports = userReports.filter((r) => isCurrentMonth(r.expense_date));
      const monthTotal   = monthReports.reduce((s, r) => s + Number(r.amount), 0);
      users.push({
        userId,
        userName:     `${firstName} ${lastName}`.trim() || t.expenseReports.unknownUser,
        allReports:   userReports,
        monthReports,
        monthTotal,
        pendingCount:  userReports.filter((r) => r.status === "PENDING").length,
        approvedCount: userReports.filter((r) => r.status === "APPROVED").length,
      });
    }
    users.sort((a, b) => a.userName.localeCompare(b.userName));
    result.push({ companyId, companyName, users, monthTotal: users.reduce((s, u) => s + u.monthTotal, 0) });
  }
  return result.sort((a, b) => a.companyName.localeCompare(b.companyName));
}

// ── StatusBadge ───────────────────────────────────────────────
function StatusBadge({ status }: { status: StatusType }) {
  const { t } = useTranslation();
  const STATUS_CONFIG: Record<StatusType, { label: string; className: string }> = {
    DRAFT:      { label: t.expenseReports.statusDraft,      className: "status-badge status--draft"      },
    PENDING:    { label: t.expenseReports.statusPending,    className: "status-badge status--pending"    },
    APPROVED:   { label: t.expenseReports.statusApproved,   className: "status-badge status--approved"   },
    REIMBURSED: { label: t.expenseReports.statusReimbursed, className: "status-badge status--reimbursed" },
    REJECTED:   { label: t.expenseReports.statusRejected,   className: "status-badge status--rejected"   },
  };
  const { label, className } = STATUS_CONFIG[status] ?? { label: status, className: "status-badge" };
  return <span className={className}>{label}</span>;
}

// ── StatusNotifPills ──────────────────────────────────────────
function StatusNotifPills({ pendingCount, approvedCount, canSeePending }: { pendingCount: number; approvedCount: number; canSeePending: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="notif-pills">
      {canSeePending && pendingCount > 0 && (
        <span className="notif-pill notif-pill--pending" title={`${pendingCount} ${pendingCount !== 1 ? t.expenseReports.pendingAlertPlural : t.expenseReports.pendingAlertSingle}`}>
          <span className="notif-dot notif-dot--pending" />{pendingCount} {t.expenseReports.pendingLabel}
        </span>
      )}
      {approvedCount > 0 && (
        <span className="notif-pill notif-pill--approved" title={`${approvedCount} ${approvedCount !== 1 ? t.expenseReports.approvedAlertPlural : t.expenseReports.approvedAlertSingle}`}>
          <span className="notif-dot notif-dot--approved" />{approvedCount} {t.expenseReports.approvedLabel}
        </span>
      )}
      {(!canSeePending || pendingCount === 0) && approvedCount === 0 && (
        <span className="notif-pill notif-pill--empty">{t.expenseReports.noAlerts}</span>
      )}
    </div>
  );
}

// ── CompanyFilterPanel ────────────────────────────────────────
interface CompanyFilterPanelProps {
  filter:      CompanyFilter;
  onChange:    (f: CompanyFilter) => void;
  categories:  string[];
  years:       string[];
  months:      { value: string; label: string }[];
  onClose:     () => void;
}

function CompanyFilterPanel({ filter, onChange, categories, years, months, onClose }: CompanyFilterPanelProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const hasFilter = filter.month || filter.year || filter.category || filter.status;

  const set = (key: keyof CompanyFilter, value: string) =>
    onChange({ ...filter, [key]: value });

  const clear = () => onChange({ month: "", year: "", category: "", status: "" });

  return (
    <div className="company-filter-panel" ref={ref} onClick={(e) => e.stopPropagation()}>
      <div className="company-filter-header">
        <span className="company-filter-title">
          {/* Filter icon */}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          {t.expenseReports.filterTitle}
        </span>
        {hasFilter && (
          <button className="company-filter-clear" onClick={clear}>
            {t.expenseReports.filterClear}
          </button>
        )}
      </div>

      <div className="company-filter-grid">
        {/* Année */}
        <div className="company-filter-field">
          <label className="company-filter-label">{t.expenseReports.filterYear}</label>
          <select className="company-filter-select" value={filter.year} onChange={(e) => set("year", e.target.value)}>
            <option value="">{t.expenseReports.filterAll}</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Mois */}
        <div className="company-filter-field">
          <label className="company-filter-label">{t.expenseReports.filterMonth}</label>
          <select className="company-filter-select" value={filter.month} onChange={(e) => set("month", e.target.value)}>
            <option value="">{t.expenseReports.filterAll}</option>
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        {/* Catégorie */}
        <div className="company-filter-field">
          <label className="company-filter-label">{t.expenseReports.filterCategory}</label>
          <select className="company-filter-select" value={filter.category} onChange={(e) => set("category", e.target.value)}>
            <option value="">{t.expenseReports.filterAll}</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Statut */}
        <div className="company-filter-field">
          <label className="company-filter-label">{t.expenseReports.filterStatus}</label>
          <select className="company-filter-select" value={filter.status} onChange={(e) => set("status", e.target.value)}>
            <option value="">{t.expenseReports.filterAll}</option>
            {(["PENDING", "APPROVED", "REJECTED", "REIMBURSED"] as StatusType[]).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ── UserExpenseTable ──────────────────────────────────────────
interface UserExpenseTableProps {
  userGroup:      UserGroup;
  showMonthOnly:  boolean;
  avatarColor:    string;
  canSeePending:  boolean;
  companyFilter:  CompanyFilter;
  selectedIds:    Set<string>;
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
}

function UserExpenseTable({ userGroup, showMonthOnly, avatarColor, canSeePending, companyFilter, selectedIds, setSelectedIds }: UserExpenseTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const translateCat = t.categoryTypes;

  const reports = useMemo(() => {
    let base = showMonthOnly ? userGroup.monthReports : userGroup.allReports;

    if (companyFilter.year) {
      base = base.filter((r) => {
        const d = r.expense_date ? new Date(r.expense_date) : null;
        return d && String(d.getFullYear()) === companyFilter.year;
      });
    }
    if (companyFilter.month) {
      base = base.filter((r) => {
        if (!r.expense_date) return false;
        const d   = new Date(r.expense_date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        return key === companyFilter.month;
      });
    }
    if (companyFilter.category) {
      base = base.filter((r) => r.category?.item_type === companyFilter.category);
    }
    if (companyFilter.status) {
      base = base.filter((r) => r.status === companyFilter.status);
    }
    return base;
  }, [userGroup, showMonthOnly, companyFilter]);

  const totalPages  = Math.max(1, Math.ceil(reports.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const sliced      = reports.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // selectedIds est partagé au niveau de l'entreprise (plusieurs utilisateurs).
  // On ne compte ici que les uuid qui appartiennent à CET utilisateur.
  const selectedCountForUser = useMemo(
    () => reports.filter((r) => selectedIds.has(r.uuid)).length,
    [reports, selectedIds]
  );

  const isAllSelected   = reports.length > 0 && selectedCountForUser === reports.length;
  const isIndeterminate = selectedCountForUser > 0 && selectedCountForUser < reports.length;

  const handleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        reports.forEach((r) => next.delete(r.uuid));
      } else {
        reports.forEach((r) => next.add(r.uuid));
      }
      return next;
    });
  };

  const handleSelectOne = (uuid: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  return (
    <div className="user-expense-block">
      <button className="user-expense-header" onClick={() => setOpen((p) => !p)} aria-expanded={open}>
        <div className={`row-avatar ${avatarColor}`} style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
          {userInitials(userGroup.allReports[0]?.user?.first_name ?? "", userGroup.allReports[0]?.user?.last_name ?? "")}
        </div>
        <span className="user-expense-name">{userGroup.userName}</span>
        <StatusNotifPills pendingCount={userGroup.pendingCount} approvedCount={userGroup.approvedCount} canSeePending={canSeePending} />
        <div className="user-expense-meta">
          <span className="month-total-label">{new Date().toLocaleString(navigator.language, { month: "long", year: "numeric" })}</span>
          <span className={`month-total-amount ${userGroup.monthTotal > 0 ? "month-total-amount--has" : "month-total-amount--empty"}`}>
            {userGroup.monthTotal > 0 ? formatAmount(userGroup.monthTotal) : t.expenseReports.noExpenses}
          </span>
        </div>
        <span className={`expense-count-pill ${avatarColor}`}>
          {userGroup.allReports.length} {userGroup.allReports.length !== 1 ? t.expenseReports.reportCountPlural : t.expenseReports.reportCountSingle}
        </span>
        <span className={`chevron-icon ${open ? "chevron-open" : ""}`}>&#8964;</span>
      </button>

      {open && (
        <div className="expense-table-wrap">
          {reports.length === 0 ? (
            <p className="empty-state">{t.expenseReports.noExpensesThisMonth}</p>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "14px" }}>
                {showMonthOnly ? (
                  <div className="month-filter-banner" style={{ borderBottom: "none", flex: 1 }}>
                    {t.expenseReports.showingExpensesFor} {new Date().toLocaleString(navigator.language, { month: "long", year: "numeric" })} {t.expenseReports.only}
                  </div>
                ) : <div style={{ flex: 1 }} />}

                {/* --- CONTENEUR DES 3 BOUTONS D'EXPORT (par utilisateur) --- */}
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>

                  {/* BOUTON CSV (Bleu par défaut) */}
                  <button
                    className="btn-transfer"
                    style={{ marginTop: "8px", marginBottom: "8px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const toExport = selectedCountForUser > 0 ? reports.filter(r => selectedIds.has(r.uuid)) : reports;
                      downloadCSV(toExport, userGroup.userName);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {selectedCountForUser > 0 ? `CSV (${selectedCountForUser})` : 'CSV'}
                  </button>

                  {/* BOUTON XML (Vert) */}
                  <button
                    className="btn-transfer"
                    style={{ marginTop: "8px", marginBottom: "8px", backgroundColor: "#0f766e" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const toExport = selectedCountForUser > 0 ? reports.filter(r => selectedIds.has(r.uuid)) : reports;
                      downloadXML(toExport, userGroup.userName);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="16 18 22 12 16 6" />
                      <polyline points="8 6 2 12 8 18" />
                    </svg>
                    {selectedCountForUser > 0 ? `XML (${selectedCountForUser})` : 'XML'}
                  </button>

                  {/* BOUTON PDF (Rouge) */}
                  <button
                    className="btn-transfer"
                    style={{ marginTop: "8px", marginBottom: "8px", backgroundColor: "#b91c1c" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const toExport = selectedCountForUser > 0 ? reports.filter(r => selectedIds.has(r.uuid)) : reports;
                      downloadPDF(toExport, userGroup.userName);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                    {selectedCountForUser > 0 ? `PDF (${selectedCountForUser})` : 'PDF'}
                  </button>

                </div>
              </div>
              <div className="table-scroll">
                <table className="expense-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          style={{ cursor: "pointer" }}
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isIndeterminate; }}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>{t.expenseReports.tableSupplier}</th>
                      <th>{t.expenseReports.tableCategory}</th>
                      <th>{t.expenseReports.tableDate}</th>
                      <th>{t.expenseReports.tableAmount}</th>
                      <th>{t.expenseReports.tableVat}</th>
                      <th>{t.expenseReports.tableStatus}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sliced.map((report) => (
                      <tr key={report.uuid} className="expense-row" onClick={() => navigate(`/expense-reports/${report.uuid}`)}>
                        <td
                          style={{ textAlign: "center" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            style={{ cursor: "pointer" }}
                            checked={selectedIds.has(report.uuid)}
                            onChange={() => handleSelectOne(report.uuid)}
                          />
                        </td>
                        <td><span className="supplier-name">{report.supplier_name || <span className="text-muted">—</span>}</span></td>
                        <td><span className="badge badge--neutral">{translateCat[report.category?.item_type?.toLowerCase() as keyof typeof translateCat] || report.category?.item_type || "—"}</span></td>
                        <td className="text-secondary">{formatDate(report.expense_date)}</td>
                        <td className="amount-cell">{formatAmount(Number(report.amount))}</td>
                        <td className="text-secondary">{report.vat_amount != null ? formatAmount(Number(report.vat_amount)) : "—"}</td>
                        <td><StatusBadge status={report.status} /></td>
                        <td className="arrow-cell">&#8250;</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="pagination">
                  <span className="pagination-info">Page {currentPage} / {totalPages}</span>
                  <div className="pagination-controls">
                    <button className="pg-btn" disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                      .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
                        acc.push(p); return acc;
                      }, [])
                      .map((p, idx) => p === "…"
                        ? <span key={`dots-${idx}`} className="pg-dots">…</span>
                        : <button key={p} className={`pg-btn ${p === currentPage ? "pg-btn--active" : ""}`} onClick={() => setPage(p as number)}>{p}</button>
                      )}
                    <button className="pg-btn" disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── CompanyExpenseBlock ───────────────────────────────────────
interface CompanyExpenseBlockProps {
  group:                CompanyGroup;
  searchQuery:          string;
  colorIndex:           number;
  canSeePending:        boolean;
  allReports:           ExpenseReport[];
  companyMatchesSearch: boolean;
}

function CompanyExpenseBlock({ group, searchQuery, colorIndex, canSeePending, allReports, companyMatchesSearch }: CompanyExpenseBlockProps) {
  const { t } = useTranslation();
  const [open, setOpen]           = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filter, setFilter]       = useState<CompanyFilter>({ month: "", year: "", category: "", status: "" });
  const avatarColor               = COLOR_CLASSES[colorIndex % COLOR_CLASSES.length];

  // Sélection partagée par TOUS les utilisateurs de cette entreprise
  // → permet d'exporter des notes de frais de plusieurs collaborateurs en une fois
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Build filter options from this company's reports only
  const companyReports = useMemo(
    () => allReports.filter((r) => (r.company?.uuid ?? "__no_company__") === group.companyId),
    [allReports, group.companyId]
  );

  const { categories, years, months } = useMemo(() => {
    const catSet  = new Set<string>();
    const yearSet = new Set<string>();
    const monthSet = new Map<string, string>(); // "YYYY-MM" -> label

    for (const r of companyReports) {
      if (r.category?.item_type) catSet.add(r.category.item_type);
      if (r.expense_date) {
        const d     = new Date(r.expense_date);
        const year  = String(d.getFullYear());
        const mKey  = `${year}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const mLbl  = d.toLocaleString(navigator.language, { month: "long", year: "numeric" });
        yearSet.add(year);
        monthSet.set(mKey, mLbl);
      }
    }

    return {
      categories: [...catSet].sort(),
      years:      [...yearSet].sort().reverse(),
      months:     [...monthSet.entries()]
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([value, label]) => ({ value, label })),
    };
  }, [companyReports]);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || companyMatchesSearch) return group.users;
    return group.users.filter((u) =>
      u.userName.toLowerCase().includes(q) ||
      u.allReports[0]?.user?.email?.toLowerCase().includes(q)
    );
  }, [group.users, searchQuery, companyMatchesSearch]);

  const showMonthOnly    = searchQuery.trim().length > 0;
  const displayMonthTotal = filteredUsers.reduce((s, u) => s + u.monthTotal, 0);
  const hasFilter        = !!(filter.month || filter.year || filter.category || filter.status);

  // Filtered total for display when filter is active
  const filteredTotal = useMemo(() => {
    if (!hasFilter) return null;
    let base = companyReports;
    if (filter.year)     base = base.filter((r) => r.expense_date && String(new Date(r.expense_date).getFullYear()) === filter.year);
    if (filter.month)    base = base.filter((r) => {
      if (!r.expense_date) return false;
      const d = new Date(r.expense_date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` === filter.month;
    });
    if (filter.category) base = base.filter((r) => r.category?.item_type === filter.category);
    if (filter.status)   base = base.filter((r) => r.status === filter.status);
    return base.reduce((s, r) => s + Number(r.amount), 0);
  }, [hasFilter, companyReports, filter]);

  // Liste réelle des notes de frais de l'entreprise à exporter, en respectant
  // le panneau de filtre (année/mois/catégorie/statut), sinon le mode "ce mois-ci"
  // déclenché par la recherche, sinon tout l'historique de l'entreprise.
  const companyExportReports = useMemo(() => {
    let base = companyReports;

    if (filter.year)     base = base.filter((r) => r.expense_date && String(new Date(r.expense_date).getFullYear()) === filter.year);
    if (filter.month)    base = base.filter((r) => {
      if (!r.expense_date) return false;
      const d = new Date(r.expense_date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` === filter.month;
    });
    if (filter.category) base = base.filter((r) => r.category?.item_type === filter.category);
    if (filter.status)   base = base.filter((r) => r.status === filter.status);

    if (!hasFilter && showMonthOnly) {
      base = base.filter((r) => isCurrentMonth(r.expense_date));
    }

    return base;
  }, [companyReports, filter, hasFilter, showMonthOnly]);

  // Nombre de notes actuellement sélectionnées, tous utilisateurs confondus, pour cette entreprise
  const selectedCountForCompany = selectedIds.size;

  return (
    <div className="company-block">
      
      {/* Modification ici : remplacement du button par une div cliquable */}
      <div
        className="company-header"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((p) => !p);
          }
        }}
      >
        <div className={`company-avatar ${avatarColor}`}>{companyInitials(group.companyName)}</div>
        <span className="company-name">{group.companyName}</span>

        {/* Filter trigger button */}
        <div className="company-filter-trigger-wrapper" onClick={(e) => e.stopPropagation()}>
          <button
            className={`company-filter-btn ${hasFilter ? "company-filter-btn--active" : ""} ${filterOpen ? "company-filter-btn--open" : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
            title={t.expenseReports.filterTitle}
            aria-label={t.expenseReports.filterTitle}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            {hasFilter && <span className="company-filter-dot" />}
          </button>

          {filterOpen && (
            <CompanyFilterPanel
              filter={filter}
              onChange={setFilter}
              categories={categories}
              years={years}
              months={months}
              onClose={() => setFilterOpen(false)}
            />
          )}
        </div>

        {/* Month total + filter button */}
        <div className="company-header-meta">
          <span className="company-month-label">{t.expenseReports.monthTotal}</span>
          <span className="company-month-amount">
            {hasFilter && filteredTotal !== null
              ? formatAmount(filteredTotal)
              : formatAmount(showMonthOnly ? displayMonthTotal : group.monthTotal)}
          </span>
        </div>

        <span className={`company-count ${avatarColor}`}>
          {filteredUsers.length} {filteredUsers.length !== 1 ? t.expenseReports.userCountPlural : t.expenseReports.userCountSingle}
          {searchQuery && filteredUsers.length !== group.users.length ? ` / ${group.users.length}` : ""}
        </span>
        <span className={`chevron-icon ${open ? "chevron-open" : ""}`}>&#8964;</span>
      </div>

      {open && (
        <div className="company-users-list">
          {filteredUsers.length === 0 ? (
            <p className="empty-state">{t.expenseReports.noUsersMatch}</p>
          ) : (
            <>
              {/* --- EXPORT GROUPÉ AU NIVEAU DE L'ENTREPRISE (plusieurs collaborateurs) --- */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f1f5f9" }}>
                <button
                  className="btn-transfer"
                  onClick={(e) => {
                    e.stopPropagation();
                    const toExport = selectedCountForCompany > 0
                      ? companyExportReports.filter((r) => selectedIds.has(r.uuid))
                      : companyExportReports;
                    downloadCSV(toExport, group.companyName);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {selectedCountForCompany > 0 ? `CSV (${selectedCountForCompany})` : 'CSV'}
                </button>

                <button
                  className="btn-transfer"
                  style={{ backgroundColor: "#0f766e" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const toExport = selectedCountForCompany > 0
                      ? companyExportReports.filter((r) => selectedIds.has(r.uuid))
                      : companyExportReports;
                    downloadXML(toExport, group.companyName);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                  {selectedCountForCompany > 0 ? `XML (${selectedCountForCompany})` : 'XML'}
                </button>

                <button
                  className="btn-transfer"
                  style={{ backgroundColor: "#b91c1c" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const toExport = selectedCountForCompany > 0
                      ? companyExportReports.filter((r) => selectedIds.has(r.uuid))
                      : companyExportReports;
                    downloadPDF(toExport, group.companyName);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  {selectedCountForCompany > 0 ? `PDF (${selectedCountForCompany})` : 'PDF'}
                </button>
              </div>

              {filteredUsers.map((userGroup) => (
                <UserExpenseTable
                  key={userGroup.userId}
                  userGroup={userGroup}
                  showMonthOnly={showMonthOnly}
                  avatarColor={avatarColor}
                  canSeePending={canSeePending}
                  companyFilter={filter}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExpenseReportForm() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const { canManage, isAccountant, isLoading: isUserLoading } = useCurrentUser();
  const canSeePending = !isUserLoading && canManage && !isAccountant;

      const [isAuthReady, setIsAuthReady] = useState(false);

   useEffect(() => {
          const auth = getAuth(); 
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthReady(true);
          });
          return () => unsubscribe();
        }, []);

  const { data, isLoading: isLoadingExpenseReports, isError, error } = useQuery<ExpenseReport[], Error>({
    queryKey: ["fetchAllExpenseReports"],
    queryFn:  () => apiCall<ExpenseReport[]>("/expense-reports", { method: "GET" }),
                      enabled: isAuthReady,

  });

  const { data: currentUser, isLoading: isLoadingMe } = useQuery<User, Error>({
    queryKey: ["fetchCurrentUser"],
    queryFn:  async () => apiCall<User>("/users/me", { method: "GET" }),
                      enabled: isAuthReady,

  });

  const userToGlimpse    = currentUser as any;
  const userSubscription =
    userToGlimpse?.Company?.Subscription ||
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;
  const currentPlan = userSubscription?.status === "active" ? userSubscription.plan_code : "none";
  const isLoading   = isLoadingExpenseReports || isLoadingMe;

  const groups = useMemo(() => groupData(data ?? [], t), [data, t]);

  const visibleGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      const companyMatch = g.companyName.toLowerCase().includes(q);
      const userMatch = g.users.some(
        (u) =>
          u.userName.toLowerCase().includes(q) ||
          u.allReports[0]?.user?.email?.toLowerCase().includes(q)
      );
      return companyMatch || userMatch;
    });
  }, [groups, searchQuery]);

  const stats = useMemo(() => {
    const reports      = data ?? [];
    const monthReports = reports.filter((r) => isCurrentMonth(r.expense_date));
    return {
      total:      reports.length,
      companies:  groups.length,
      monthTotal: monthReports.reduce((s, r) => s + Number(r.amount), 0),
      pending:    reports.filter((r) => r.status === "PENDING").length,
      approved:   reports.filter((r) => r.status === "APPROVED").length,
      reimbursed: reports.filter((r) => r.status === "REIMBURSED").length,
    };
  }, [data, groups]);

  if (isLoading) {
    return (
      <div className="dashboard-wrapper">
        <Navbar /><TopHeader />
        <div className="dashboard-content">
          <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName="la gestion des Notes de frais">
            <div className="page-header"><h1>{t.expenseReports.title}</h1></div>
            <div className="loading-grid">{[...Array(3)].map((_, i) => <div key={i} className="skeleton-block" />)}</div>
          </FeatureGate>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="dashboard-wrapper">
        <Navbar /><TopHeader />
        <div className="dashboard-content">
          <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName="la gestion des Notes de frais">
            <div className="page-header"><h1>{t.expenseReports.title}</h1></div>
            <div className="error-state"><span className="error-icon">⚠</span><p>{t.expenseReports.failedToLoad} {error.message}</p></div>
          </FeatureGate>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Navbar /><TopHeader />
      <div className="dashboard-content">
        <div className="page-header">
          <div>
            <h1>{t.expenseReports.title}</h1>
            <p className="page-subtitle">{t.expenseReports.subtitle}</p>
          </div>
        </div>

        <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName="la gestion des Notes de frais">
          <div className={`stats-row ${canSeePending ? "stats-row--6" : "stats-row--5"}`}>
            <div className="stat-card"><span className="stat-label">{t.expenseReports.statTotalReports}</span><span className="stat-value">{stats.total}</span></div>
            <div className="stat-card"><span className="stat-label">{t.expenseReports.statCompanies}</span><span className="stat-value">{stats.companies}</span></div>
            <div className="stat-card"><span className="stat-label">{t.expenseReports.statMonthTotal}</span><span className="stat-value stat-value--amount">{formatAmount(stats.monthTotal)}</span></div>
            {canSeePending && (
              <div className="stat-card stat-card--pending"><span className="stat-label">{t.expenseReports.statPending}</span><span className="stat-value stat-value--pending">{stats.pending}</span></div>
            )}
            <div className="stat-card"><span className="stat-label">{t.expenseReports.statApproved}</span><span className="stat-value stat-value--approved">{stats.approved}</span></div>
            <div className="stat-card"><span className="stat-label">{t.expenseReports.statReimbursed}</span><span className="stat-value stat-value--reimbursed">{stats.reimbursed}</span></div>
          </div>

          {searchQuery && (
            <div className="search-hint">{t.expenseReports.showingSearchMatch} <strong>"{searchQuery}"</strong></div>
          )}

          <div className="toolbar">
            <div className="search-wrapper">
              <span className="search-icon">🔍</span>
              <input type="text" className="search-input" placeholder={`      ${t.expenseReports.searchPlaceholder}`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              {searchQuery && <button className="search-clear" onClick={() => setSearchQuery("")}>✕</button>}
            </div>
          </div>

          {visibleGroups.length === 0 ? (
            <div className="empty-page"><p>{t.expenseReports.noReportsFound}</p></div>
          ) : (
            visibleGroups.map((group, idx) => {
              const q = searchQuery.trim().toLowerCase();
              const companyMatchesSearch = !!q && group.companyName.toLowerCase().includes(q);
              return (
                <CompanyExpenseBlock
                  key={group.companyId}
                  group={group}
                  searchQuery={searchQuery}
                  colorIndex={idx}
                  canSeePending={canSeePending}
                  allReports={data ?? []}
                  companyMatchesSearch={companyMatchesSearch}
                />
              );
            })
          )}
        </FeatureGate>
      </div>
    </div>
  );
}